import { useEffect, useRef, type RefObject } from 'react';
import type { ISeriesApi, UTCTimestamp } from 'lightweight-charts';
import type { Candle } from '@pipyar/core';
import { useSymbol } from '@/lib/useSymbol';
import { useQuote } from '@/features/alerts/api';

/**
 * Bars longer than this are not started from a tick: their boundaries follow
 * the trading session rather than the clock (see ingest/sessions.ts), so a new
 * H4/D1/W1 bar waits for the page's next refetch instead of being guessed.
 */
const MAX_SYNTHESISED_STEP = 3600;

/** The series' bar length, from the smallest gap between recent bars. */
function barStep(candles: Candle[]): number | null {
  let step: number | null = null;
  for (let i = Math.max(1, candles.length - 12); i < candles.length; i++) {
    const d = candles[i]!.time - candles[i - 1]!.time;
    if (d > 0 && (step === null || d < step)) step = d;
  }
  return step;
}

/**
 * Moves a chart's last candle with the live price.
 *
 * The page's own query still fetches candles and analysis on its timer; this
 * only patches the newest bar in place with `series.update` as each tick is
 * pushed, which costs nothing on the network and next to nothing to draw. The
 * tick is the forming M1 bar, so its high and low are folded in as well — a
 * spike between two pushes still shows on the wick.
 *
 * When the tick belongs to the NEXT bar (a new minute on M1, a new five on M5),
 * that bar is started from it, so the chart does not freeze at a bar boundary
 * waiting for the refetch. The refetch then replaces it with the real one.
 */
export function useLiveBar(
  seriesRef: RefObject<ISeriesApi<'Candlestick'> | null>,
  candles: Candle[],
): void {
  const { symbol } = useSymbol();
  const quote = useQuote(symbol).data;

  /** The newest bar as currently drawn, including live changes. */
  const working = useRef<Candle | null>(null);
  const step = useRef<number | null>(null);

  // Fresh data from the page resets the working bar to what it says.
  useEffect(() => {
    working.current = candles.length > 0 ? candles[candles.length - 1]! : null;
    step.current = barStep(candles);
  }, [candles]);

  useEffect(() => {
    const series = seriesRef.current;
    const last = working.current;
    if (!series || !last || !quote || quote.symbol !== symbol) return;

    const t = quote.barTime;
    // A tick from before the chart's newest bar (a stale quote, or a series
    // that runs ahead of M1) has nothing to say about it.
    if (t < last.time) return;

    let bar: Candle;
    const s = step.current;
    if (s === null || t < last.time + s) {
      bar = {
        ...last,
        close: quote.price,
        high: Math.max(last.high, quote.high, quote.price),
        low: Math.min(last.low, quote.low, quote.price),
      };
    } else {
      if (s > MAX_SYNTHESISED_STEP) return;
      const time = last.time + Math.floor((t - last.time) / s) * s;
      // When the tick's own M1 bar opened exactly at the new bar's start, its
      // high and low belong to the new bar too. The open is the first price
      // seen either way; the refetch replaces it with the broker's.
      const fromStart = t === time;
      bar = {
        time,
        open: quote.price,
        high: fromStart ? Math.max(quote.high, quote.price) : quote.price,
        low: fromStart ? Math.min(quote.low, quote.price) : quote.price,
        close: quote.price,
        tickVolume: 0,
        spread: 0,
        realVolume: 0,
      };
    }

    if (
      bar.time === last.time &&
      bar.close === last.close &&
      bar.high === last.high &&
      bar.low === last.low
    ) {
      return;
    }

    working.current = bar;
    try {
      // `true` permits replacing a bar that is not the series' final point —
      // needed where a chart pads the right edge with future whitespace.
      series.update(
        { time: bar.time as UTCTimestamp, open: bar.open, high: bar.high, low: bar.low, close: bar.close },
        true,
      );
    } catch {
      /* chart disposed mid-update, or a time the series cannot take */
    }
  }, [quote, symbol, seriesRef]);
}
